import _classCallCheck from "/home/runner/work/mdlz-mds-mondelez-afh-co-uk/mdlz-mds-mondelez-afh-co-uk/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/home/runner/work/mdlz-mds-mondelez-afh-co-uk/mdlz-mds-mondelez-afh-co-uk/node_modules/@babel/runtime/helpers/esm/createClass.js";
import { invariant, millisecondsToSeconds, secondsToMilliseconds, noop } from 'motion-utils';
import { style } from '../render/dom/style.mjs';
import { getFinalKeyframe } from './keyframes/get-final.mjs';
import { hydrateKeyframes } from './keyframes/hydrate.mjs';
import { startWaapiAnimation } from './waapi/start-waapi-animation.mjs';
import { applyGeneratorOptions } from './waapi/utils/apply-generator.mjs';
var animationMaps = new WeakMap();
var animationMapKey = function animationMapKey(name, pseudoElement) {
  return "".concat(name, ":").concat(pseudoElement);
};
function getAnimationMap(element) {
  var map = animationMaps.get(element) || new Map();
  animationMaps.set(element, map);
  return map;
}
/**
 * NativeAnimation implements AnimationPlaybackControls for the browser's Web Animations API.
 */
var NativeAnimation = /*#__PURE__*/function () {
  function NativeAnimation(options) {
    var _this = this;
    _classCallCheck(this, NativeAnimation);
    /**
     * If we already have an animation, we don't need to instantiate one
     * and can just use this as a controls interface.
     */
    if ("animation" in options) {
      this.animation = options.animation;
      return;
    }
    var element = options.element,
      name = options.name,
      unresolvedKeyframes = options.keyframes,
      pseudoElement = options.pseudoElement,
      _options$allowFlatten = options.allowFlatten,
      allowFlatten = _options$allowFlatten === void 0 ? false : _options$allowFlatten;
    var transition = options.transition;
    this.allowFlatten = allowFlatten;
    /**
     * Stop any existing animations on the element before reading existing keyframes.
     *
     * TODO: Check for VisualElement before using animation state. This is a fallback
     * for mini animate(). Do this when implementing NativeAnimationExtended.
     */
    var animationMap = getAnimationMap(element);
    var key = animationMapKey(name, pseudoElement || "");
    var currentAnimation = animationMap.get(key);
    currentAnimation && currentAnimation.stop();
    /**
     * TODO: If these keyframes aren't correctly hydrated then we want to throw
     * run an instant animation.
     */
    var keyframes = hydrateKeyframes(element, name, unresolvedKeyframes, pseudoElement);
    invariant(typeof transition.type !== "string", "animateMini doesn't support \"type\" as a string. Did you mean to import { spring } from \"motion\"?");
    transition = applyGeneratorOptions(transition);
    this.animation = startWaapiAnimation(element, name, keyframes, transition, pseudoElement);
    if (transition.autoplay === false) {
      this.animation.pause();
    }
    this.removeAnimation = function () {
      return animationMap.delete(key);
    };
    this.animation.onfinish = function () {
      if (!pseudoElement) {
        style.set(element, name, getFinalKeyframe(keyframes, transition));
      } else {
        _this.commitStyles();
      }
      _this.cancel();
    };
    /**
     * TODO: Check for VisualElement before using animation state.
     */
    animationMap.set(key, this);
  }
  _createClass(NativeAnimation, [{
    key: "play",
    value: function play() {
      this.animation.play();
    }
  }, {
    key: "pause",
    value: function pause() {
      this.animation.pause();
    }
  }, {
    key: "complete",
    value: function complete() {
      this.animation.finish();
    }
  }, {
    key: "cancel",
    value: function cancel() {
      try {
        this.animation.cancel();
      } catch (e) {}
      this.removeAnimation();
    }
  }, {
    key: "stop",
    value: function stop() {
      var state = this.state;
      if (state === "idle" || state === "finished") {
        return;
      }
      this.commitStyles();
      this.cancel();
    }
    /**
     * WAAPI doesn't natively have any interruption capabilities.
     *
     * In this method, we commit styles back to the DOM before cancelling
     * the animation.
     *
     * This is designed to be overridden by NativeAnimationExtended, which
     * will create a renderless JS animation and sample it twice to calculate
     * its current value, "previous" value, and therefore allow
     * Motion to also correctly calculate velocity for any subsequent animation
     * while deferring the commit until the next animation frame.
     */
  }, {
    key: "commitStyles",
    value: function commitStyles() {
      var _this$animation$commi, _this$animation;
      (_this$animation$commi = (_this$animation = this.animation).commitStyles) === null || _this$animation$commi === void 0 ? void 0 : _this$animation$commi.call(_this$animation);
    }
  }, {
    key: "duration",
    get: function get() {
      var _this$animation$effec, _this$animation$effec2;
      console.log((_this$animation$effec = this.animation.effect) === null || _this$animation$effec === void 0 ? void 0 : _this$animation$effec.getComputedTiming());
      var duration = ((_this$animation$effec2 = this.animation.effect) === null || _this$animation$effec2 === void 0 ? void 0 : _this$animation$effec2.getComputedTiming().duration) || 0;
      return millisecondsToSeconds(Number(duration));
    }
  }, {
    key: "time",
    get: function get() {
      return millisecondsToSeconds(Number(this.animation.currentTime) || 0);
    },
    set: function set(newTime) {
      this.animation.currentTime = secondsToMilliseconds(newTime);
    }
    /**
     * The playback speed of the animation.
     * 1 = normal speed, 2 = double speed, 0.5 = half speed.
     */
  }, {
    key: "speed",
    get: function get() {
      return this.animation.playbackRate;
    },
    set: function set(newSpeed) {
      this.animation.playbackRate = newSpeed;
    }
  }, {
    key: "state",
    get: function get() {
      return this.animation.playState;
    }
  }, {
    key: "startTime",
    get: function get() {
      return Number(this.animation.startTime);
    }
  }, {
    key: "finished",
    get: function get() {
      return this.animation.finished;
    }
  }, {
    key: "flatten",
    value: function flatten() {
      if (this.allowFlatten) {
        var _this$animation$effec3;
        (_this$animation$effec3 = this.animation.effect) === null || _this$animation$effec3 === void 0 ? void 0 : _this$animation$effec3.updateTiming({
          easing: "linear"
        });
      }
    }
    /**
     * Attaches a timeline to the animation, for instance the `ScrollTimeline`.
     */
  }, {
    key: "attachTimeline",
    value: function attachTimeline(timeline) {
      this.animation.timeline = timeline;
      this.animation.onfinish = null;
      return noop;
    }
    /**
     * Allows the animation to be awaited.
     *
     * @deprecated Use `finished` instead.
     */
  }, {
    key: "then",
    value: function then(onResolve, onReject) {
      return this.finished.then(onResolve).catch(onReject);
    }
  }]);
  return NativeAnimation;
}();
export { NativeAnimation };